"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestAccessForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="request-access-form" ref={ref} className="py-24 bg-light-background">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <FormProvider className="bg-light-background border-[var(--border-md)] border-accent rounded-lg shadow-soft hover:shadow-medium hover:-translate-y-2 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] p-8" formId="request_access_form_data_room" sectionName="request-access-form" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          investor_type: "",
          message: "",
          agree_terms: false
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          message: {
            minLength: 10,
            maxLength: 500
          },
          agree_terms: {
            required: true
          }
        }} submitText="Request Access" successMessage="Thank you! Your access request has been received. We'll review it and get back to you shortly." errorMessage="We couldn't submit your request. Please check your information and try again.">
            <RequestAccessFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function RequestAccessFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center space-y-6">
        <div className="p-6 bg-accent-light border border-accent rounded-lg">
          <p className="text-foreground-accent font-semibold text-lg">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="border-accent text-primary hover:bg-accent-light">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-display text-primary">
          Request Access Form
        </h2>
        <p className="text-muted-foreground">
          Fill in your details to access our secure data room
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Minimum 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Maximum 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="border-muted focus-visible:border-accent focus-visible:ring-accent/50" />
                </FormControl>
                <FormDescription>
                  Enter your legal name as it appears on your ID.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="border-muted focus-visible:border-accent focus-visible:ring-accent/50" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[\d\s+()-]+$/,
            message: "Invalid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+233 24 000 0000" {...field} className="border-muted focus-visible:border-accent focus-visible:ring-accent/50" />
                </FormControl>
                <FormDescription>International format preferred.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="investor_type" render={({
          field
        }) => <FormItem>
                <FormLabel>Investor Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="border-muted focus-visible:border-accent focus-visible:ring-accent/50">
                      <SelectValue placeholder="Select your investor type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Individual">Individual</SelectItem>
                    <SelectItem value="Corporate">Corporate</SelectItem>
                    <SelectItem value="Family Office">Family Office</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          minLength: {
            value: 10,
            message: "Minimum 10 characters required"
          },
          maxLength: {
            value: 500,
            message: "Maximum 500 characters allowed"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Reason for Access</FormLabel>
                <FormControl>
                  <textarea placeholder="Briefly explain why you need access to the data room..." {...field} rows={4} className="border-input selection:bg-primary selection:text-primary-foreground dark:bg-input/30 flex w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive resize-none" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agree_terms" rules={{
          required: "You must agree to the terms and conditions"
        }} render={({
          field
        }) => <FormItem>
                <div className="flex items-start gap-3">
                  <FormControl>
                    <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded border-muted text-primary focus:ring-accent focus:ring-2 focus:ring-offset-2" />
                  </FormControl>
                  <div className="flex-1">
                    <FormLabel className="font-normal cursor-pointer">
                      I agree to the terms and conditions
                    </FormLabel>
                    <FormDescription>
                      You must agree before submitting.
                    </FormDescription>
                  </div>
                </div>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover text-foreground-primary font-semibold py-3 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:shadow-medium hover:-translate-y-0.5 active:translate-y-0 relative after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-[var(--duration-fast)] hover:after:w-full">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Submitting...
              </> : "Request Access"}
          </Button>
        </div>
      </Form>
    </div>;
}